import {
  MagnifyingGlassIcon,
  PlusIcon,
  UserIcon,
  CheckCircleIcon,
  XCircleIcon,
  ShieldCheckIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';
import { FC, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { userApi, User as UserType } from '@/api/users';

const UsersContainer = styled.div`
  min-height: 100vh;
  background: var(--gray-50);
`;

const UsersContent = styled.div`
  padding: 2.5rem 0;
`;

const Header = styled.header`
  max-width: 1560px;
  margin: 0 auto;
  padding: 0 2rem;
  margin-bottom: 2rem;
`;

const HeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const Title = styled.h1`
  font-size: 1.875rem;
  font-weight: 700;
  color: var(--gray-900);
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1.25rem;
  background: var(--primary-600);
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }

  &:hover {
    background: var(--primary-700);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(234, 88, 12, 0.3);
  }
`;

const SearchBox = styled.div`
  position: relative;
  width: 300px;

  svg {
    position: absolute;
    left: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    width: 1.25rem;
    height: 1.25rem;
    color: var(--gray-400);
  }

  input {
    width: 100%;
    padding: 0.5rem 1rem 0.5rem 2.5rem;
    border: 1px solid var(--gray-300);
    border-radius: 0.5rem;
    font-size: 0.875rem;

    &:focus {
      outline: none;
      border-color: var(--primary-600);
      box-shadow: 0 0 0 3px rgba(234, 88, 12, 0.1);
    }
  }
`;

const Main = styled.main`
  max-width: 1560px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.25rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 0.5rem;
  box-shadow: var(--shadow-sm);

  h3 {
    font-size: 0.875rem;
    color: var(--gray-600);
    margin-bottom: 0.5rem;
  }

  p {
    font-size: 2rem;
    font-weight: 700;
    color: var(--gray-900);
  }
`;

const UsersTable = styled.div`
  background: white;
  border-radius: 0.75rem;
  box-shadow: var(--shadow-sm);
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: var(--gray-50);
  border-bottom: 1px solid var(--gray-200);
`;

const Th = styled.th`
  padding: 0.875rem 1rem;
  text-align: left;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--gray-700);
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid var(--gray-100);

  &:hover {
    background: var(--gray-50);
  }

  &:last-child {
    border-bottom: none;
  }
`;

const Td = styled.td`
  padding: 1rem;
  font-size: 0.875rem;
  color: var(--gray-700);
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Avatar = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 9999px;
  background: var(--primary-100);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  color: var(--primary-700);
  font-size: 0.875rem;
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;

  .name {
    font-weight: 500;
    color: var(--gray-900);
  }

  .email {
    font-size: 0.75rem;
    color: var(--gray-500);
  }
`;

const Badge = styled.span<{
  $variant?: 'success' | 'error' | 'warning' | 'info';
}>`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.625rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;

  ${(props) => {
    switch (props.$variant) {
      case 'success':
        return `
          background: #d1fae5;
          color: #065f46;
        `;
      case 'error':
        return `
          background: #fee2e2;
          color: #991b1b;
        `;
      case 'warning':
        return `
          background: #fef3c7;
          color: #92400e;
        `;
      case 'info':
      default:
        return `
          background: #dbeafe;
          color: #1e40af;
        `;
    }
  }}

  svg {
    width: 0.875rem;
    height: 0.875rem;
  }
`;

const RoleBadge = styled.span`
  display: inline-block;
  padding: 0.125rem 0.5rem;
  background: var(--gray-100);
  color: var(--gray-700);
  border-radius: 0.25rem;
  font-size: 0.75rem;
  font-weight: 500;
  margin-right: 0.25rem;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ActionButton = styled.button`
  padding: 0.375rem 0.75rem;
  border: 1px solid var(--gray-300);
  background: white;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: var(--primary-600);
    color: var(--primary-600);
  }
`;

const DeleteButton = styled(ActionButton)`
  &:hover {
    border-color: var(--error-600);
    color: var(--error-600);
    background: var(--error-50);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;

  h3 {
    font-size: 1.125rem;
    font-weight: 600;
    color: var(--gray-900);
    margin-bottom: 0.5rem;
  }

  p {
    color: var(--gray-500);
    font-size: 0.875rem;
    margin-bottom: 1.5rem;
  }
`;

const Users: FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [users, setUsers] = useState<UserType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, _setPage] = useState(1);
  const [_totalPages, _setTotalPages] = useState(1);

  // Load users from backend
  useEffect(() => {
    loadUsers();
  }, [page, searchTerm]);

  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await userApi.getUsers({
        page,
        per_page: 20,
        search: searchTerm || undefined,
      });
      setUsers(response.users || []);
      _setTotalPages(response.pages || 1);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (user: UserType) => {
    if (!confirm(`Are you sure you want to delete ${user.email}?`)) {
      return;
    }

    try {
      await userApi.deleteUser(user.id);
      loadUsers();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to delete user');
    }
  };

  const handleUnlock = async (user: UserType) => {
    try {
      await userApi.unlockUser(user.id);
      loadUsers();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to unlock user');
    }
  };

  const getInitials = (user: UserType) => {
    const first = user.first_name?.[0] || '';
    const last = user.last_name?.[0] || '';
    return (first + last).toUpperCase() || user.email[0].toUpperCase();
  };

  const stats = {
    total: users.length,
    active: users.filter((u) => u.is_active).length,
    verified: users.filter((u) => u.is_verified).length,
    admins: users.filter((u) => u.roles?.includes('ROLE_ADMIN')).length,
  };

  if (loading) {
    return (
      <UsersContainer>
        <UsersContent>
          <Header>
            <HeaderTop>
              <Title>User Management</Title>
            </HeaderTop>
          </Header>
          <Main>
            <div style={{ textAlign: 'center', padding: '4rem' }}>
              <div
                style={{
                  width: '2rem',
                  height: '2rem',
                  border: '3px solid var(--gray-200)',
                  borderTopColor: 'var(--primary-600)',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite',
                  margin: '0 auto',
                }}
              />
            </div>
          </Main>
        </UsersContent>
      </UsersContainer>
    );
  }

  if (error) {
    return (
      <UsersContainer>
        <UsersContent>
          <Header>
            <HeaderTop>
              <Title>User Management</Title>
            </HeaderTop>
          </Header>
          <Main>
            <div
              style={{
                padding: '2rem',
                background: 'var(--error-50)',
                border: '1px solid var(--error-200)',
                borderRadius: '0.5rem',
                color: 'var(--error-700)',
              }}
            >
              {error}
            </div>
          </Main>
        </UsersContent>
      </UsersContainer>
    );
  }

  return (
    <UsersContainer>
      <UsersContent>
        <Header>
          <HeaderTop>
            <Title>User Management</Title>
            <HeaderActions>
              <SearchBox>
                <MagnifyingGlassIcon />
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </SearchBox>
              <AddButton onClick={() => navigate('/users/new')}>
                <PlusIcon />
                Add User
              </AddButton>
            </HeaderActions>
          </HeaderTop>
        </Header>

        <Main>
          <StatsGrid>
            <StatCard>
              <h3>Total Users</h3>
              <p>{stats.total}</p>
            </StatCard>
            <StatCard>
              <h3>Active</h3>
              <p>{stats.active}</p>
            </StatCard>
            <StatCard>
              <h3>Verified</h3>
              <p>{stats.verified}</p>
            </StatCard>
            <StatCard>
              <h3>Admins</h3>
              <p>{stats.admins}</p>
            </StatCard>
          </StatsGrid>

          {users.length > 0 ? (
            <UsersTable>
              <Table>
                <Thead>
                  <Tr>
                    <Th>User</Th>
                    <Th>Tenant</Th>
                    <Th>Roles</Th>
                    <Th>Status</Th>
                    <Th>Last Login</Th>
                    <Th>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {users.map((user) => (
                    <Tr key={user.id}>
                      <Td>
                        <UserInfo>
                          <Avatar>{getInitials(user)}</Avatar>
                          <UserDetails>
                            <span className="name">
                              {user.first_name || user.last_name
                                ? `${user.first_name} ${user.last_name}`.trim()
                                : 'No name'}
                            </span>
                            <span className="email">{user.email}</span>
                          </UserDetails>
                        </UserInfo>
                      </Td>
                      <Td>{user.tenant_name || '-'}</Td>
                      <Td>
                        {user.roles?.map((role) => (
                          <RoleBadge key={role}>
                            {role.replace('ROLE_', '')}
                          </RoleBadge>
                        ))}
                      </Td>
                      <Td>
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0.25rem',
                          }}
                        >
                          {user.is_active ? (
                            <Badge $variant="success">
                              <CheckCircleIcon />
                              Active
                            </Badge>
                          ) : (
                            <Badge $variant="error">
                              <XCircleIcon />
                              Inactive
                            </Badge>
                          )}
                          {user.is_verified && (
                            <Badge $variant="info">
                              <ShieldCheckIcon />
                              Verified
                            </Badge>
                          )}
                          {user.locked_until &&
                            new Date(user.locked_until) > new Date() && (
                              <Badge $variant="warning">
                                <LockClosedIcon />
                                Locked
                              </Badge>
                            )}
                        </div>
                      </Td>
                      <Td>
                        {user.last_login_at
                          ? new Date(user.last_login_at).toLocaleDateString()
                          : 'Never'}
                      </Td>
                      <Td>
                        <ActionButtons>
                          <ActionButton
                            onClick={() => navigate(`/users/edit/${user.id}`)}
                          >
                            Edit
                          </ActionButton>
                          {user.locked_until &&
                            new Date(user.locked_until) > new Date() && (
                              <ActionButton onClick={() => handleUnlock(user)}>
                                Unlock
                              </ActionButton>
                            )}
                          <DeleteButton onClick={() => handleDelete(user)}>
                            Delete
                          </DeleteButton>
                        </ActionButtons>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </UsersTable>
          ) : (
            <EmptyState>
              <UserIcon
                style={{
                  width: '3rem',
                  height: '3rem',
                  margin: '0 auto 1rem',
                  color: 'var(--gray-400)',
                }}
              />
              <h3>No users found</h3>
              <p>Try adjusting your search or add your first user</p>
              <AddButton onClick={() => navigate('/users/new')}>
                <PlusIcon />
                Add User
              </AddButton>
            </EmptyState>
          )}
        </Main>
      </UsersContent>
    </UsersContainer>
  );
};

export default Users;
